import { useEffect, useSyncExternalStore } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Persisted under the same key the pre-paint script in index.html reads. */
// `slate.` is the ESTATE storage namespace, not a brand — coil already writes this same key at
// my.app.co.il (same docit-ui source), so every existing theme choice survives the appcoil
// cutover. Do not re-prefix or alias this key for a site profile: it would mass-sign-out /
// reset every my.app.co.il session and risk a pre-paint/key-drift mismatch.
export const THEME_STORAGE_KEY = "slate.theme";

interface ThemeState {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      preference: "system",
      setPreference: (preference) => set({ preference }),
    }),
    {
      name: THEME_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/** Imperative setter for non-React callers (action definitions, tests). */
export const setThemePreference = (preference: ThemePreference) =>
  useThemeStore.getState().setPreference(preference);

export const getThemePreference = () => useThemeStore.getState().preference;

function matchDark(): MediaQueryList | null {
  return typeof window !== "undefined" && window.matchMedia ? window.matchMedia(DARK_QUERY) : null;
}

function subscribeToSystem(onChange: () => void) {
  const mql = matchDark();
  if (!mql) {
    return () => {};
  }
  mql.addEventListener("change", onChange);
  return () => mql.removeEventListener("change", onChange);
}

function getSystemSnapshot(): ResolvedTheme {
  return matchDark()?.matches ? "dark" : "light";
}

/** The OS preference, as a live value. Exported so scenes can preview "System". */
export function useSystemTheme(): ResolvedTheme {
  return useSyncExternalStore(subscribeToSystem, getSystemSnapshot, () => "light" as const);
}

export function resolveTheme(preference: ThemePreference, system: ResolvedTheme): ResolvedTheme {
  return preference === "system" ? system : preference;
}

/** Writes the resolved theme to <html data-theme>. Mount exactly once, in the app root. */
export function useApplyTheme(): ResolvedTheme {
  const preference = useThemeStore((s) => s.preference);
  const system = useSystemTheme();
  const resolved = resolveTheme(preference, system);

  useEffect(() => {
    document.documentElement.dataset.theme = resolved;
  }, [resolved]);

  return resolved;
}

/**
 * Read and change the theme. Switching is a CSS variable swap on <html>, so nothing below
 * this hook re-renders because of a theme change.
 */
export function useTheme() {
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);
  const system = useSystemTheme();

  return {
    preference,
    system,
    resolved: resolveTheme(preference, system),
    setPreference,
  };
}
